import { PublicClientApplication, PopupRequest } from '@azure/msal-browser'

const MICROSOFT_CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID
const MICROSOFT_AUTHORITY = import.meta.env.VITE_MICROSOFT_AUTHORITY ?? 'https://login.microsoftonline.com/common'
const DEFAULT_SCOPES = ['User.Read']

let msalApp: PublicClientApplication | null = null

const getConfiguredScopes = (): string[] => {
  const configured = import.meta.env.VITE_MICROSOFT_SCOPES
  if (!configured) {
    return DEFAULT_SCOPES
  }

  const scopes = configured
    .split(',')
    .map((scope: string) => scope.trim())
    .filter(Boolean)

  return scopes.length > 0 ? scopes : DEFAULT_SCOPES
}

const ensureMsalInstance = (): PublicClientApplication => {
  if (!MICROSOFT_CLIENT_ID) {
    throw new Error('VITE_MICROSOFT_CLIENT_ID is not configured. Unable to sign in with Microsoft.')
  }

  if (!msalApp) {
    if (typeof window === 'undefined') {
      throw new Error('Microsoft authentication can only be initialized in a browser environment.')
    }

    msalApp = new PublicClientApplication({
      auth: {
        clientId: MICROSOFT_CLIENT_ID,
        authority: MICROSOFT_AUTHORITY,
        redirectUri: window.location.origin
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false
      }
    })
  }

  return msalApp
}

export const signInWithMicrosoft = async (): Promise<string | null> => {
  if (typeof window === 'undefined') {
    throw new Error('Microsoft sign-in is only available in the browser.')
  }

  const instance = ensureMsalInstance()
  if (typeof instance.initialize === 'function') {
    await instance.initialize()
  }

  const loginRequest: PopupRequest = {
    scopes: getConfiguredScopes()
  }

  const result = await instance.loginPopup(loginRequest)
  return result.idToken ?? null
}
